const MODEL_FILTER = 'filter.p.m.hoxo.compatible_models';
const TYPE_FILTER = 'filter.p.product_type';

export function searchIntent(query) {
  const text = query.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  let type;
  if (/\b(screen\s*protectors?|screen\s*protection|tempered\s*glass|screenprotector|cameraglas|schutzglas|panzerglas|verre\s*trempe|protector\s*(de\s*)?pantalla)\b/.test(text)) type = 'Screenprotectors & cameraglas';
  else if (/\b(hoesjes?|cases?|covers?|hulles?|coques?|fundas?|carcasas?)\b/.test(text)) type = 'Hoesjes';
  if (!type) return {};
  const iphone = text.match(/\biphone\s*(\d{1,2}|duo|air)\s*(pro\s*max|pro|plus|mini|air|e)?\b/);
  const galaxy = text.match(/\b(?:samsung\s*)?(?:galaxy\s*)?([sa]\s*\d{2})\s*(ultra|plus|fe|\+)?(?=\s|$)/);
  let model;
  if (iphone && !galaxy) {
    const suffix = (iphone[2] || '').replace(/pro\s*max/, 'pro max').replace(/\s+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const number = iphone[1].replace(/\b\w/g, c => c.toUpperCase());
    model = `iPhone ${number}${suffix ? ' ' + suffix : ''}`;
  } else if (galaxy && !iphone) {
    let suffix = (galaxy[2] || '').replace('+', 'plus');
    suffix = suffix === 'fe' ? 'FE' : suffix.replace(/\b\w/g, c => c.toUpperCase());
    model = `Samsung Galaxy ${galaxy[1].replace(/\s/g, '').toUpperCase()}${suffix ? ' ' + suffix : ''}`;
  }
  return { type, model };
}

export function searchUrl(action, query, current = window.location.href) {
  const url = new URL(action, current);
  url.search = '';
  url.searchParams.set('type', 'product');
  url.searchParams.set('q', query.trim());
  const { type, model } = searchIntent(query);
  const language = typeof document === 'undefined' ? 'en' : document.documentElement.lang.split('-')[0];
  const types = {
    Hoesjes: { en: ['Hoesjes'], nl: ['Hoesjes'], de: ['Hüllen'], fr: ['Coques'], es: ['Fundas'] },
    'Screenprotectors & cameraglas': {
      en: ['Screenprotectors & cameraglas'], nl: ['Screenprotectors & cameraglas'],
      de: ['Displayschutz & Kameraschutz', 'Display- & Kameraschutz'],
      fr: ['Protections d’écran et de caméra'], es: ['Protectores de pantalla y cámara']
    }
  };
  if (type) (types[type]?.[language] || [type]).forEach(value => url.searchParams.append(TYPE_FILTER, value));
  if (model) url.searchParams.set(MODEL_FILTER, model);
  const preview = new URL(current).searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  return url;
}

function setupSearch(root = document) {
  root.querySelectorAll('form[role="search"]').forEach(form => {
    if (form.dataset.hoxoSearchReady) return;
    form.dataset.hoxoSearchReady = 'true';
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    form.addEventListener('submit', event => {
      event.preventDefault();
      window.location.assign(searchUrl(form.action, input.value));
    });
  });
  root.querySelectorAll('[data-hoxo-search]').forEach(wrapper => {
    if (wrapper.dataset.searchReady) return;
    wrapper.dataset.searchReady = 'true';
    const input = wrapper.querySelector('input[name="q"]');
    const results = wrapper.querySelector('.hx-suggestions');
    let timer, controller;
    const close = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      controller?.abort();
      close();
      const query = input.value.trim();
      if (query.length < 2) return;
      timer = setTimeout(async () => {
        const request = new AbortController();
        controller = request;
        const target = searchUrl(wrapper.dataset.endpoint, query);
        const endpoint = new URL(target);
        endpoint.searchParams.set('section_id', 'hoxo-search-suggestions');
        try {
          const response = await fetch(endpoint, { signal: request.signal });
          if (!response.ok) return;
          const html = await response.text();
          if (request.signal.aborted || input.value.trim() !== query || !wrapper.contains(document.activeElement)) return;
          const fragment = new DOMParser().parseFromString(html, 'text/html');
          results.replaceChildren();
          fragment.querySelectorAll('[data-hoxo-search-result]').forEach(item => {
            const link = document.createElement('a');
            const href = new URL(item.getAttribute('href'), window.location.origin);
            if (href.origin !== window.location.origin) return;
            link.href = href.href;
            link.textContent = item.textContent.trim();
            results.append(link);
          });
          if (!results.children.length) {
            const empty = document.createElement('p');
            empty.textContent = wrapper.dataset.empty;
            results.append(empty);
          }
          const all = document.createElement('a');
          all.href = target.href;
          all.textContent = wrapper.dataset.viewAll;
          results.append(all);
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        } catch (error) {
          if (error.name !== 'AbortError') close();
        }
      }, 250);
    });
    wrapper.addEventListener('keydown', event => {
      if (event.key === 'Escape') { close(); input.focus(); }
      if (!results.hidden && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        const links = [...results.querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
        if (next < 0) input.focus();
        else links[Math.min(next, links.length - 1)]?.focus();
      }
    });
    wrapper.addEventListener('focusout', event => { if (!wrapper.contains(event.relatedTarget)) close(); });
  });
}

if (typeof document !== 'undefined') {
  const current = new URL(window.location.href);
  const searchForm = document.querySelector('form[role="search"]');
  const isSearch = searchForm && current.pathname === new URL(searchForm.action).pathname;
  const hasFilters = [...current.searchParams.keys()].some(key => key.startsWith('filter.'));
  if (isSearch && current.searchParams.get('q') && !hasFilters) {
    const target = searchUrl(searchForm.action, current.searchParams.get('q'));
    if (target.searchParams.has(TYPE_FILTER)) window.location.replace(target);
  }
  setupSearch();
  document.addEventListener('shopify:section:load', event => setupSearch(event.target));
}
